import { Button } from "components/DesignSystem/Button/Button";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { FormikForm } from "components/FormikForm/FormikForm";
import { PriceInput } from "components/PriceInput/PriceInput";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { Field, FieldProps, FormikValues } from "formik";
import { creditNoteSchema } from "formValidations/creditNoteSchema";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import {
  CreditNote,
  useCreateCreditNoteMutation,
  useUpdateCreditNoteMutation,
} from "store/apis/creditNotes";
import { useGetInvoiceCustomerQuery } from "store/apis/invoices";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";

type CreditNoteModalProps = ModalProps & {
  customerId: string;
  entityId: string;
  creditNote?: CreditNote;
};

export const CreditNoteModal = ({
  isOpen,
  close,
  customerId,
  entityId,
  creditNote,
}: CreditNoteModalProps) => {
  const { successToast, alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();

  const isEditMode = Boolean(creditNote?.uuid);

  const { data: customer } = useGetInvoiceCustomerQuery(
    {
      customerId: customerId,
      groupId: groupId,
      entityId: entityId!,
    },
    {
      skip: !groupId || !entityId || !customerId,
    }
  );

  const [createCreditNote, { isLoading: isCreating }] =
    useCreateCreditNoteMutation();
  const [updateCreditNote, { isLoading: isUpdating }] =
    useUpdateCreditNoteMutation();

  const isSubmitting = isCreating || isUpdating;

  const initialValues = {
    postedDate: creditNote?.date || dayjs().format(YYYY_MM_DD),
    creditAmount: creditNote?.amount || 0,
    description: creditNote?.description || "",
  };

  const onSubmit = async (values: FormikValues) => {
    const payload = {
      date: values.postedDate,
      amount: values.creditAmount,
      description: values.description,
    };

    try {
      if (isEditMode) {
        await updateCreditNote({
          groupId,
          entityId,
          customerId,
          creditNoteId: creditNote!.uuid,
          payload,
        }).unwrap();
        successToast({
          title: "Credit note updated successfully",
          message: `For ${customer?.name}`,
        });
      } else {
        await createCreditNote({
          groupId,
          entityId,
          customerId,
          payload,
        }).unwrap();
        successToast({
          title: "Credit note added successfully",
          message: `For ${customer?.name}`,
        });
      }
      close();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content asChild useCustomOverlay>
        <FormikForm
          initialValues={initialValues}
          validationSchema={creditNoteSchema}
          onSubmit={onSubmit}
          enableReinitialize
          validateOnMount
        >
          {({ submitForm, isValid }) => (
            <>
              <Modal.Header>
                <Modal.Title>
                  {isEditMode ? "Edit" : "Add"} Credit Note
                </Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body className="t-flex t-flex-col t-gap-5">
                <Field name="postedDate">
                  {({ field }: FieldProps) => (
                    <DateInput
                      {...field}
                      label="Creation date"
                      required
                      block
                      portalId="credit-note-date"
                    />
                  )}
                </Field>
                <PriceInput
                  name="creditAmount"
                  label="Credit amount"
                  required
                  placeholder="0.00"
                />
                <TextArea
                  name="description"
                  label="Description"
                  required
                  placeholder="Enter description"
                  block
                  rows={3}
                />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button>Cancel</Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  isLoading={isSubmitting}
                  disabled={isSubmitting || !isValid}
                  onClick={submitForm}
                >
                  {isEditMode ? "Save" : "Add"}
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};

export const AddCreditNoteModal = CreditNoteModal;
